import type { IRowNode } from "ag-grid-community";
import { computed } from "vue";

import { colorsByTheme } from "~/constants/style";
import type { VisualizationItemTask } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { makeStringComparator } from "~/utils/comparator";
import { getOrdersBetween } from "~/utils/orderManager";

import type { Actions } from "..";

/** Drag related actions */
export default (actions: Actions) => ({
  updateGhostStyle: (options?: { text?: string; hide?: boolean; icon?: string }) => {
    const pageStore = usePageStore();
    const { text, hide, icon } = options ?? {};
    // TODO this is all a hack because I can't figure out how to bind the styles properly
    const colors = computed(() => colorsByTheme[pageStore.theme]);
    const ghost = document.querySelectorAll(".ag-dnd-ghost")[0] as HTMLDivElement;
    if (!ghost) {
      // oxlint-disable-next-line dart/no-set-timeout
      setTimeout(() => actions.visualization.updateGhostStyle(options), 100);
      return;
    }

    if (hide) {
      ghost.style.cursor = "default";
      return;
    }

    ghost.style.opacity = "100";
    ghost.style.backgroundColor = colors.value.bgLt;
    ghost.style.borderColor = colors.value.borderLt;
    ghost.style.color = colors.value.textMd;

    if (ghost.children[0]) {
      if (icon) {
        (ghost.children[0] as HTMLDivElement).innerHTML = icon;
      }
      (ghost.children[0] as HTMLDivElement).style.color = colors.value.btnHvyHover;
    }
    if (text) {
      ghost.children[1].innerHTML = text;
    }
  },
  getOrdersForMovingNodes: async (
    primaryMovingNode: IRowNode<VisualizationItemTask>,
    movingNodes: IRowNode<VisualizationItemTask>[],
    overRow: IRowNode<VisualizationItemTask> | undefined
  ) => {
    const dataStore = useDataStore();

    const sortedSiblingNodes = primaryMovingNode.parent?.childrenAfterSort;
    const filteredSiblingNodes = primaryMovingNode.parent?.childrenAfterFilter;
    if (!sortedSiblingNodes || !filteredSiblingNodes) {
      return undefined;
    }

    const filteredSiblingDuids = new Set(filteredSiblingNodes.map((e) => e.data?.duid));
    if (overRow?.data && !filteredSiblingDuids.has(overRow.data.duid)) {
      return undefined;
    }

    const movingRowDuids = new Set(movingNodes.map((e) => e.data?.duid));
    const sortedStationarySiblingTaskDuids = sortedSiblingNodes
      .map((e) => e.data?.duid)
      .filter((e): e is string => !!e && filteredSiblingDuids.has(e) && !movingRowDuids.has(e));
    const sortedStationarySiblingTasks = dataStore.getTasksByDuidsIfLoaded(sortedStationarySiblingTaskDuids, {
      includeTrashed: false,
      includeDraft: false,
    });
    const primaryMovingTask = primaryMovingNode.data
      ? dataStore.getTaskByDuidIfLoaded(primaryMovingNode.data.duid)
      : undefined;

    let startOrder = "";
    let endOrder = "";
    if (!overRow) {
      if (sortedStationarySiblingTasks.length > 0) {
        startOrder = sortedStationarySiblingTasks
          .map((e) => e.order)
          .reduce((prev, curr) => (prev > curr ? prev : curr));
      }
    } else {
      const overTask = overRow.data ? dataStore.getTaskByDuidIfLoaded(overRow.data.duid) : undefined;
      const goingUp = (primaryMovingNode.rowIndex ?? 0) > (overRow.rowIndex ?? 0);
      const newPos =
        sortedStationarySiblingTasks.findIndex((e) => e.duid === overRow.data?.duid) +
        ((primaryMovingTask?.order || 0) > (overTask?.order || 0) ? 0 : 1);
      if (newPos < 0 || newPos > sortedStationarySiblingTasks.length) {
        return undefined;
      }
      if (newPos !== 0) {
        startOrder = sortedStationarySiblingTasks[newPos - 1].order;
      }
      if (newPos !== sortedStationarySiblingTasks.length) {
        endOrder = sortedStationarySiblingTasks[newPos].order;
      }
      // normalize the order for weird cases with sorts
      const currentOrder = primaryMovingTask?.order;
      if (
        currentOrder === undefined ||
        (goingUp && (endOrder === "" || endOrder >= currentOrder)) ||
        (!goingUp && (startOrder === "" || startOrder <= currentOrder))
      ) {
        return undefined;
      }
      if (startOrder >= endOrder) {
        if (goingUp) {
          startOrder = "";
        } else {
          endOrder = "";
        }
      }
    }

    return getOrdersBetween(startOrder, endOrder, movingNodes.length);
  },
  reorderRows: async (
    primaryMovingRow: IRowNode<VisualizationItemTask>,
    movingRows: IRowNode<VisualizationItemTask>[],
    overRow: IRowNode<VisualizationItemTask> | undefined,
    options: { noBackend?: boolean; indentLevelChange?: number } = {}
  ) => {
    const { noBackend = false, indentLevelChange = 0 } = options;

    const appStore = useAppStore();
    const dataStore = useDataStore();
    const visualization = appStore.getActiveVisualization();

    if (primaryMovingRow.data === undefined) {
      return;
    }

    // this block handles cases where the moving nodes are not all siblings (e.g. select a parent and its children)
    const realMovingRows = movingRows.filter(
      (e): e is IRowNode<VisualizationItemTask> & { data: VisualizationItemTask } => !!e.data
    );
    const realMovingDuids = new Set(realMovingRows.map((e) => e.data.duid));
    const movingRowToMovingAncestorMap = new Map(
      realMovingRows.map((base) => {
        let ancestor = base;
        let curr = base;
        for (let i = 0; i < 1000; i += 1) {
          const { parent } = curr;
          if (!parent?.data) {
            break;
          }
          curr = parent as IRowNode<VisualizationItemTask> & { data: VisualizationItemTask };
          if (realMovingDuids.has(curr.data.duid)) {
            ancestor = curr;
          }
        }
        return [base.data.duid, ancestor];
      })
    );
    const levelizedPrimaryMovingRow = movingRowToMovingAncestorMap.get(primaryMovingRow.data.duid);
    if (!levelizedPrimaryMovingRow) {
      return;
    }
    const levelizedMovingRows = realMovingRows.filter(
      (e) => movingRowToMovingAncestorMap.get(e.data.duid)?.data?.duid === e.data.duid
    );

    const parentDuid = levelizedPrimaryMovingRow.parent?.data?.duid;
    if (levelizedMovingRows.some((e) => e.parent?.data?.duid !== parentDuid)) {
      return;
    }

    const levelizedMovingTasks = dataStore.getTasksByDuidsIfLoaded(
      levelizedMovingRows.map((e) => e.data.duid),
      { includeTrashed: false, includeDraft: false }
    );

    // Check if we need to change parent (either through indent change or direct drop on new parent)
    let shouldChangeParent = indentLevelChange !== 0;
    let targetParentTask = null;

    // When dragging straight down (no indent change), check if dropping on a new parent
    if (indentLevelChange === 0 && overRow?.data) {
      // Check if the overRow is at a different level than the moving row's siblings--this indicates we're dropping onto a potential new parent
      const currentParent = levelizedPrimaryMovingRow.parent;
      const overRowParent = overRow.parent;

      // If dropping on a row that's not a sibling (different parent), make the overRow the new parent
      if (currentParent !== overRowParent && !realMovingDuids.has(overRow.data.duid)) {
        shouldChangeParent = true;
        targetParentTask = dataStore.getTaskByDuidIfLoaded(overRow.data.duid) ?? null;
      }
    }

    if (shouldChangeParent) {
      if (indentLevelChange !== 0) {
        let startRow = overRow ?? null;
        if (startRow === null || (startRow.data !== undefined && realMovingDuids.has(startRow.data.duid))) {
          const totalRows = startRow === null ? visualization.count() : (startRow.rowIndex ?? visualization.count());
          for (let i = totalRows - 1; i >= 0; i -= 1) {
            const row = visualization.getRowByIndex(i);
            if (row?.data !== undefined && !realMovingDuids.has(row.data.duid)) {
              startRow = row;
              break;
            }
          }
        }
        const stack = [startRow];
        while (stack[0] !== null) {
          const curr = stack[0];
          stack.unshift(curr.parent ?? null);
          if (curr.data?.duid === levelizedPrimaryMovingRow.data.duid) {
            return;
          }
        }
        const newLevel = levelizedPrimaryMovingRow.uiLevel + indentLevelChange;
        const newParent = stack[Math.min(newLevel + 1, stack.length - 1)];
        targetParentTask = newParent?.data ? (dataStore.getTaskByDuidIfLoaded(newParent.data.duid) ?? null) : null;
      }

      dataStore.changeParent(
        levelizedMovingTasks.map((e) => ({ task: e, order: e.order })),
        targetParentTask,
        { noBackend }
      );
    }

    const newOrders = await actions.visualization.getOrdersForMovingNodes(
      levelizedPrimaryMovingRow,
      levelizedMovingRows,
      overRow
    );
    if (newOrders === undefined) {
      return;
    }

    dataStore.updateTasks(
      levelizedMovingTasks.sort(makeStringComparator((e) => e.order)).map((e, i) => ({
        duid: e.duid,
        order: newOrders[i],
      })),
      noBackend
        ? { noUndo: true, noBackend: true, skipRemap: true }
        : {
            makeDescription: (isActive, content) => `mov${isActive ? "ing" : "ed"} ${content}`,
          }
    );
  },
  getSiblingOverRow: (
    primaryMovingNode: IRowNode<VisualizationItemTask>,
    rowIndex: number
  ): IRowNode<VisualizationItemTask> | undefined => {
    const appStore = useAppStore();
    const visualization = appStore.getActiveVisualization();

    let curr = visualization.getRowByIndex(rowIndex);
    while (curr) {
      if (curr === primaryMovingNode) {
        return undefined;
      }
      if (curr.parent === primaryMovingNode.parent) {
        return curr;
      }
      curr = curr.parent ?? undefined;
    }
    return curr;
  },
  moveTaskUp: async () => {
    const currents = actions.visualization.getCurrentRows();
    if (actions.visualization.currentRowIsCreateModal(currents)) {
      return;
    }
    const topMovingRow = currents.reduce((prev, curr) =>
      (prev.rowIndex as number) < (curr.rowIndex as number) ? prev : curr
    );
    if (topMovingRow.rowIndex === null || topMovingRow.rowIndex === 0) {
      return;
    }
    const overRow = actions.visualization.getSiblingOverRow(topMovingRow, topMovingRow.rowIndex - 1);
    if (!overRow) {
      return;
    }

    await actions.visualization.reorderRows(topMovingRow, currents, overRow);
  },
  moveTaskDown: async () => {
    const appStore = useAppStore();
    const visualization = appStore.getActiveVisualization();

    const currents = actions.visualization.getCurrentRows();
    if (actions.visualization.currentRowIsCreateModal(currents)) {
      return;
    }
    const bottomMovingRow = currents.reduce((prev, curr) =>
      (prev.rowIndex as number) > (curr.rowIndex as number) ? prev : curr
    );
    const rowCount = visualization.count();
    if (bottomMovingRow.rowIndex === null || bottomMovingRow.rowIndex === rowCount - 1) {
      return;
    }
    const bottomMovingRowIndex = bottomMovingRow.rowIndex;
    let overRow;
    for (let i = bottomMovingRowIndex + 1; i < rowCount; i += 1) {
      const row = visualization.getRowByIndex(i);
      if (row === undefined) {
        break;
      }
      if (row.parent === bottomMovingRow.parent) {
        overRow = row;
        break;
      }
    }
    if (overRow === undefined) {
      return;
    }

    await actions.visualization.reorderRows(bottomMovingRow, currents, overRow);
  },
});
